<template>
  <v-flex
    id="reflectionResources"
    class="question"
  >
    <p class="mb-0 text-uppercase">
      {{ $t('sheet.sheetOne.reflection.hint') }}
    </p>
    <h2 class="display-3 display-3_dark mb-4">
      {{ getResourcesQuestions?.label }}
    </h2>
    <div class="content">
      <div class="alert-box pa-3">
        <p class="mb-0">
          {{ $t('sheet.sheetOne.reflection.resources.info') }}
        </p>
      </div>
    </div>
    <ul class="questions-list">
      <li
        v-if="getResourcesQuestions?.hasOwnProperty('has_resources')"
        class="mt-4"
      >
        <p
          class="header"
          :class="{ required: getResourcesQuestions?.has_resources?.required }"
        >
          {{ getResourcesQuestions.has_resources.label }}
        </p>
        <v-checkbox
          v-for="(item, index) in allResources"
          :key="index"
          v-model="hasResources"
          v-validate="{ required: getResourcesQuestions?.has_resources?.required ?? false }"
          multiple
          :label="item.description"
          :value="item.id"
          :error="errors.has('hasResources')"
          :error-messages="
            index + 1 === allResources.length ? errors.collect('hasResources') : null
          "
          :data-vv-as="$t('label.question')"
          :data-vv-name="'hasResources'"
          :disabled="readOnly"
        />
        <div
          v-if="showOtherResources"
          class="mt-4"
        >
          <p
            class="fw-600 mb-0"
            :class="{ required: getResourcesQuestions?.other_resources?.required }"
          >
            {{ getResourcesQuestions?.other_resources?.label }}
          </p>
          <v-text-field
            v-model="otherResources"
            v-validate="{ ...regularLongText(getResourcesQuestions?.other_resources?.required) }"
            :placeholder="$t('placeholders.add_text')"
            :error-messages="errors.collect('reflection_resources')"
            :data-vv-as="$t('label.question')"
            data-vv-name="reflection_resources"
            :required="getResourcesQuestions?.other_resources?.required"
            :disabled="readOnly"
          />
        </div>
      </li>
      <li
        v-if="getResourcesQuestions?.hasOwnProperty('has_strengths')"
        class="mt-4"
      >
        <p
          class="header"
          :class="{ required: getResourcesQuestions?.has_strengths?.required }"
        >
          {{ getResourcesQuestions?.has_strengths?.label }}
        </p>
        <ConfirmButtons
          v-model="hasStrengths"
          v-validate="{ required: getResourcesQuestions?.has_strengths?.required }"
          string
          :error-messages="errors.collect('hasStrengths')"
          :data-vv-as="$t('label.question')"
          :data-vv-name="'hasStrengths'"
          :disabled="readOnly"
        />
      </li>
      <li
        v-if="hasStrengths === 'yes' && getResourcesQuestions?.hasOwnProperty('strengths')"
        class="mt-4"
      >
        <p
          class="header custom lA mb-0"
          :class="{ required: getResourcesQuestions?.strengths?.required }"
        >
          {{ getResourcesQuestions?.strengths?.label }}
        </p>
        <v-text-field
          v-model="strengths"
          v-validate="{ ...regularLongText(getResourcesQuestions?.strengths?.required) }"
          :placeholder="$t('placeholders.add_text')"
          :error-messages="errors.collect('reflection_resources_2')"
          :data-vv-as="$t('label.question')"
          data-vv-name="reflection_resources_2"
          :required="getResourcesQuestions?.strengths?.required"
          :disabled="readOnly"
        />
      </li>
      <li
        v-if="getResourcesQuestions?.hasOwnProperty('need_other_resources')"
        class="mt-4"
      >
        <p
          class="header"
          :class="{ required: getResourcesQuestions?.need_other_resources?.required }"
        >
          {{ getResourcesQuestions?.need_other_resources?.label }}
        </p>
        <ConfirmButtons
          v-model="needOtherResources"
          v-validate="{ required: getResourcesQuestions?.need_other_resources?.required }"
          string
          :error-messages="errors.collect('needOtherResources')"
          :data-vv-as="$t('label.question')"
          :data-vv-name="'needOtherResources'"
          :disabled="readOnly"
        />
      </li>
      <li
        v-if="
          needOtherResources === 'yes' &&
            getResourcesQuestions?.hasOwnProperty('need_other_resources_text')
        "
        class="mt-4"
      >
        <p
          class="header custom lA mb-0"
          :class="{ required: getResourcesQuestions?.need_other_resources_text?.required }"
        >
          {{ getResourcesQuestions?.need_other_resources_text?.label }}
        </p>
        <v-text-field
          v-model="needOtherResourcesText"
          v-validate="{
            ...regularLongText(getResourcesQuestions?.need_other_resources_text?.required)
          }"
          :placeholder="$t('placeholders.add_text')"
          :error-messages="errors.collect('reflection_resources_3')"
          :data-vv-as="$t('label.question')"
          data-vv-name="reflection_resources_3"
          :required="getResourcesQuestions?.need_other_resources_text?.required"
          :disabled="readOnly"
        />
      </li>
      <li
        v-if="getResourcesQuestions.hasOwnProperty('actions_to_effects')"
        class="mt-4"
      >
        <p
          class="header"
          :class="{ required: getResourcesQuestions?.actions_to_effects?.required }"
        >
          {{ getResourcesQuestions?.actions_to_effects?.label }}
        </p>
        <v-textarea
          v-model="actionsToEffects"
          v-validate="{ ...regularLongText(getResourcesQuestions?.actions_to_effects?.required) }"
          :error-messages="errors.collect('reflection_resources_4')"
          :data-vv-as="$t('label.question')"
          :data-vv-name="'reflection_resources_4'"
          :required="getResourcesQuestions?.actions_to_effects?.required"
          :placeholder="$t('sheet.enter_text')"
          auto-grow
          :disabled="readOnly"
        />
      </li>
    </ul>
    <v-divider class="my-4" />
  </v-flex>
</template>

<script>
import validationRules from '@/constants/validationRules';

import ConfirmButtons from '@/components/widgets/sheet/ConfirmButtons';

import { createHelpers } from 'vuex-map-fields';
import { mapGetters } from 'vuex';

import sheetValidationRules from '@/mixins/sheet/sheetValidationRules';
import sheetResourcesGetter from '@/mixins/sheet/sheetResourcesGetter';

const { mapFields } = createHelpers({
    getterType: 'sheet/getReflectionResourcesField',
    mutationType: 'sheet/updateReflectionResourcesField'
});

export default {
    name: 'ReflectionResources',
    components: {
        ConfirmButtons
    },
    mixins: [sheetValidationRules, sheetResourcesGetter],
    inject: ['$validator'],
    props: {
        readOnly: {
            default: false,
            type: Boolean
        }
    },
    data () {
        return {
            validationRules
        };
    },
    computed: {
        ...mapFields([
            'hasResources',
            'otherResources',
            'strengths',
            'needOtherResourcesText',
            'actionsToEffects',
            'hasStrengths',
            'needOtherResources'
        ]),
        ...mapGetters('sheet', ['getResourcesQuestions']),
        allResources () {
            return this.getResources(this.getResourcesQuestions?.has_resources) ?? [];
        },
        showOtherResources () {
            return !!this.hasResources?.includes(this?.otherResourcesId);
        },
        otherResourcesId () {
            return this.allResources && this.allResources.find((el) => el.description === 'Inne').id;
        }
    },
    watch: {
        showOtherResources: {
            handler (val) {
                if (val !== true) {
                    this.otherResources = null;
                }
            },
            immediate: true
        },
        hasStrengths (val) {
            if (val === 'no') {
                this.strengths = null;
            }
        },
        needOtherResources (val) {
            if (val === 'no') {
                this.needOtherResourcesText = null;
            }
        }
    }
};
</script>

<style scoped></style>
